import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type SdmModuleSpecsFragment } from '../../../../../../api/fragments';
import {
    UpdateExternalBankDocument,
    type UpdateExternalBankMutation,
    type UpdateExternalBankMutationVariables,
} from '../../../../../../api/mutations/updateExternalBank';
import { type BankIntegrationSettings, type ExternalBankSettings } from '../../../../../../api/types';
import Form from '../../../../../../components/fields/Form';
import useHandleError from '../../../../../../utilities/useHandleError';
import { getBankIntegrationSettings } from '../../../../AddBankPage';
import BankFields from './BankFields';
import useBankIntegrationValidator from './useBankIntegrationValidator';

export type UpdateBankIntegrationValues = ExternalBankSettings & {
    integrationSettings: BankIntegrationSettings;
};

export type UpdateBankIntegrationFormProps = {
    module: SdmModuleSpecsFragment;
    bankId: string;
    onClose: () => void;
};

const UpdateBankIntegrationForm = ({ module, bankId, onClose }: UpdateBankIntegrationFormProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const apolloClient = useApolloClient();

    const { initialValues, provider } = useMemo(() => {
        const bank = module.banks.find(({ id }) => id === bankId);

        if (!bank) {
            return { initialValues: null, provider: null };
        }

        return {
            initialValues: {
                displayName: bank.displayName,
                identifier: bank.identifier,
                dealerCode: bank.dealerCode,
                integrationSettings: {
                    email:
                        bank.integration.__typename === 'EmailBankIntegration' ? pick('email', bank.integration) : null,
                    hlf:
                        bank.integration.__typename === 'HlfBankIntegration'
                            ? pick(
                                  [
                                      'allowedIps',
                                      'baseUrl',
                                      'submissionPartial',
                                      'editionPartial',
                                      'cancellationPartial',
                                      'reassignPartial',
                                      'privateKey',
                                      'publicCert',
                                  ],
                                  bank.integration
                              )
                            : null,
                    hlfV2:
                        bank.integration.__typename === 'HlfBankV2Integration'
                            ? pick(
                                  [
                                      'allowedIps',
                                      'baseUrl',
                                      'submissionPartial',
                                      'editionPartial',
                                      'cancellationPartial',
                                      'reassignPartial',
                                      'privateKey',
                                      'publicCert',
                                      'dealerCode',
                                  ],
                                  bank.integration
                              )
                            : null,
                    dbs:
                        bank.integration.__typename === 'DbsBankIntegration'
                            ? pick(
                                  [
                                      'allowedIps',
                                      'host',
                                      'orgId',
                                      'apiKey',
                                      'privateKey',
                                      'publicCert',
                                      'mappings',
                                      'payloadScheme',
                                  ],
                                  bank.integration
                              )
                            : null,
                    uob:
                        bank.integration.__typename === 'UobBankIntegration'
                            ? pick(
                                  [
                                      'allowedIps',
                                      'host',
                                      'clientId',
                                      'applicationId',
                                      'apiKey',
                                      'sslPrivateKey',
                                      'sslPublicCert',
                                      'encryptionPrivateKey',
                                      'encryptionPublicCert',
                                      'signaturePrivateKey',
                                  ],
                                  bank.integration
                              )
                            : null,
                    bmwFs:
                        bank.integration.__typename === 'BmwFsBankIntegration'
                            ? pick(['host', 'accessKey'], bank.integration)
                            : null,
                    maybank:
                        bank.integration.__typename === 'MaybankIntegration'
                            ? pick(
                                  [
                                      'allowedIps',
                                      'dealerId',
                                      'host',
                                      'clientId',
                                      'clientSecret',
                                      'applicationId',
                                      'apiKey',
                                      'environment',
                                      'encryptionPrivateKey',
                                      'encryptionPublicCert',
                                      'signaturePrivateKey',
                                      'signaturePublicCert',
                                      'generatedId',
                                      'generatedSecrets',
                                  ],
                                  bank.integration
                              )
                            : null,
                },
            },
            provider: bank.integration.provider,
        };
    }, [bankId, module.banks]);

    const validate = useBankIntegrationValidator(provider, true);

    const onSubmit = useHandleError<UpdateBankIntegrationValues>(
        async values => {
            const { integrationSettings, ...settings } = values;

            // submitting message
            message.loading({
                content: t('sdmModuleDetails:messages.updatingBankIntegration'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<UpdateExternalBankMutation, UpdateExternalBankMutationVariables>({
                    mutation: UpdateExternalBankDocument,
                    variables: {
                        moduleId: module.id,
                        bankId,
                        settings,
                        integrationSettings: getBankIntegrationSettings({
                            integrationProvider: provider,
                            integrationSettings,
                        }),
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('sdmModuleDetails:messages.updateSuccess'),
                key: 'primary',
            });

            // close the drawer
            onClose();
        },
        [apolloClient, provider, bankId, module.id, onClose, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="bankIntegrationForm" name="bankIntegrationForm" onSubmitCapture={handleSubmit}>
                    <BankFields provider={provider} isUpdate />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateBankIntegrationForm;
